import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { componentWithFG } from '@confluence/feature-gate-component';

import { SPACE_DIRECTORY_YOUR_SPACES_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { PERFORMANCE_SUBJECT_spaceDirectory } from '@confluence/performance/entry-points/constants.ts';
import { PerformanceStart } from '@confluence/performance/entry-points/PerformanceStart.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { CreateSpaceButton } from '@confluence/create-space/entry-points/createSpace.ts';

import {
	SearchFilter,
	LabelsFilter,
	PillFilters,
	FiltersContextProvider,
} from './Filters/index.ts';
import { Table } from './Table/index.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';
import { RecentSpacesSection } from './RecentSpacesSection/index.ts';

// Create a no-op component that just renders children when feature gate is off
const NoUFOSegment: FC<{ children: React.ReactNode; name?: string }> = ({ children }) => (
	<>{children}</>
);

// Gated UFOSegment components
const GatedUFOSegment = componentWithFG(
	'cc-disco-ufo-segment-space-directory',
	UFOSegment,
	NoUFOSegment,
);

const styles = xcss({ marginBottom: 'space.200' });
const i18n = defineMessages({
	title: {
		id: 'space-directory.title',
		defaultMessage: 'Spaces',
		description: 'Title of the Space directory screen',
	},
	allSpaces: {
		id: 'space-directory.all-spaces',
		defaultMessage: 'All spaces',
		description:
			'Heading which appears above the table (and filters) of Space directory. The table contains a list of all spaces by default.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceDirectoryContainer = styled.main({
	width: '100%',
	margin: token('space.400'),
	maxWidth: '1152px',
	minWidth: '400px',
	display: 'flex',
	justifyContent: 'center',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const PageLayoutContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AllSpacesRegion = styled.div({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '27px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	width: '100%',
	height: '32px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '33px',
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FiltersContainer = styled.div({
	width: '100%',
	'@media only screen and (min-width: 1050px)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&': {
			display: 'flex',
			justifyContent: 'space-between',
		},
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndCategory = styled.div({
	display: 'flex',
	alignItems: 'flex-end',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '17px',
	flexGrow: 1,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PillsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '17px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '9px',
});

export const SpaceDirectory: FC = () => {
	const intl = useIntl();
	const { isLicensed } = useSessionData();

	return (
		<PageLayoutContainer>
			<PerformanceStart
				subject={PERFORMANCE_SUBJECT_spaceDirectory}
				subjectId="initial"
				markBeforeMount
			/>
			{isLicensed && <ExperienceStart name={SPACE_DIRECTORY_YOUR_SPACES_EXPERIENCE} />}
			<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.title)} />
			<SpaceDirectoryContainer>
				<HeadingContainer>
					<Heading size="large">
						<FormattedMessage {...i18n.title} />
					</Heading>
					<CreateSpaceButton source="spaceDirectoryScreen" appearance="default" />
				</HeadingContainer>
				<GenericErrorBoundary
					attribution={Attribution.DISCO}
					renderOnError={() => <ErrorMessage />}
				>
					{isLicensed && (
						<GatedUFOSegment name="recent-spaces-section">
							<RecentSpacesSection />
						</GatedUFOSegment>
					)}
					<GatedUFOSegment name="all-spaces-section">
						<AllSpacesRegion role="region" aria-label={intl.formatMessage(i18n.allSpaces)}>
							{isLicensed && (
								<Box xcss={styles}>
									<Heading size="small">
										<FormattedMessage {...i18n.allSpaces} />
									</Heading>
								</Box>
							)}
							<FiltersContextProvider>
								<FiltersContainer>
									<TitleAndCategory>
										<SearchFilter />
										{isLicensed && <LabelsFilter />}
									</TitleAndCategory>
									{isLicensed && (
										<PillsContainer>
											<PillFilters />
										</PillsContainer>
									)}
								</FiltersContainer>
								<Table />
							</FiltersContextProvider>
						</AllSpacesRegion>
					</GatedUFOSegment>
				</GenericErrorBoundary>
			</SpaceDirectoryContainer>
		</PageLayoutContainer>
	);
};
